import { calendarMinuteOffset, zonedDate } from '../../../../../utils/date-time';
import { useEstabsContext } from '../../../../../contexts/EstabsContext';
import moment from 'moment';
import 'moment/locale/pt-br';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgendaAppointment } from '../types';

moment.locale('pt-br');

type AppointmentDropArgs = {
    schedule: any;
    startAt: Date;
    localDate?: string;
    startMinutes: number;
    durationMinutes: number;
};

type DragState = {
    pointerId: number;
    appointmentId: string;
    schedule: any;
    originalStart: number;
    previewStart: number;
    durationMinutes: number;
    initialPointerY: number;
    initialTop: number;
    heightPx: number;
    hasMoved: boolean;
    isValidDrop: boolean;
    phase: 'dragging' | 'dropping';
};

type DropValidationResult = {
    allowed: boolean;
    reason?: 'conflict';
};

export function NativeDayView(props: {
    appointments: AgendaAppointment[];
    date?: moment.Moment;
    settingsAgenda?: any;
    selectedRange?: { start: number; end: number } | undefined;
    setSelectedRange?: React.Dispatch<
        React.SetStateAction<{ start: number; end: number } | undefined>
    >;
    renderAppointment: (args: {
        schedule: any;
        layout?: {
            heightPx?: number;
            topPx?: number;
            isMobileViewport?: boolean;
            stackedTop?: boolean;
            stackedBottom?: boolean;
        };
    }) => React.ReactNode;
    onAppointmentDrop?: (args: AppointmentDropArgs) => Promise<void> | void;
    onInvalidAppointmentDrop?: (args: { schedule: any; reason: 'conflict' }) => void;
}) {
    const { estabSession } = useEstabsContext();
    const timezone = estabSession?.timezone;
    const {
        appointments,
        date,
        settingsAgenda,
        selectedRange,
        setSelectedRange,
        renderAppointment,
        onAppointmentDrop,
        onInvalidAppointmentDrop,
    } = props;
    const [nowMinuteTick, setNowMinuteTick] = useState(() => moment().startOf('minute').valueOf());
    const [isMobileViewport, setIsMobileViewport] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < 640 : false
    );
    const [dragState, setDragState] = useState<DragState | null>(null);
    const dragStateRef = useRef<DragState | null>(null);
    const suppressClickAppointmentIdRef = useRef<string | null>(null);

    const sorted = useMemo(() => {
        return [...(appointments || [])].sort(
            (a, b) => a.startDate.getTime() - b.startDate.getTime()
        );
    }, [appointments]);

    const scheduleGap = Number(settingsAgenda?.scheduleGap || 10);
    const safeGap = Number.isFinite(scheduleGap) && scheduleGap > 0 ? scheduleGap : 10;
    const fallbackStartMinute = 6 * 60;
    const fallbackEndMinute = 24 * 60;

    const workingSlots = useMemo(() => {
        if (!Array.isArray(settingsAgenda?.horario)) return [];
        return settingsAgenda.horario
            .map((slot: any) => ({
                start: Number(slot?.start ?? 0),
                end: Number(slot?.end ?? 0),
            }))
            .filter((slot: { start: number; end: number }) => Number.isFinite(slot.start) && Number.isFinite(slot.end) && slot.start < slot.end);
    }, [settingsAgenda?.horario]);

    const closedSlots = useMemo(() => {
        if (!Array.isArray(settingsAgenda?.closedSlots)) return [];
        return settingsAgenda.closedSlots
            .map((slot: any) => ({
                start: Number(slot?.start ?? 0),
                end: Number(slot?.end ?? 0),
            }))
            .filter((slot: { start: number; end: number }) => Number.isFinite(slot.start) && Number.isFinite(slot.end) && slot.start < slot.end);
    }, [settingsAgenda?.closedSlots]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) {
            return;
        }

        const mediaQuery = window.matchMedia('(max-width: 639px)');
        const syncViewport = () => {
            setIsMobileViewport(mediaQuery.matches);
        };

        syncViewport();

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', syncViewport);
            return () => {
                mediaQuery.removeEventListener('change', syncViewport);
            };
        }

        mediaQuery.addListener(syncViewport);
        return () => {
            mediaQuery.removeListener(syncViewport);
        };
    }, []);

    const isMinuteWithinWorkingSlot = (minute: number) =>
        // `end` exclusivo para nao pintar a borda final como "dentro" (ex.: 18:00 em slot que termina 18:00).
        workingSlots.some((slot: { start: number; end: number }) => minute >= slot.start && minute < slot.end);

    const isMinuteWithinClosedSlot = (minute: number) =>
        // `end` exclusivo pra não marcar a borda final como "fechado" (ex.: 09:00 em slot que termina 09:00).
        closedSlots.some((slot: { start: number; end: number }) => minute >= slot.start && minute < slot.end);

    const isRangeOverlappingWorkingSlot = (rangeStart: number, rangeEnd: number) =>
        workingSlots.some((slot: { start: number; end: number }) => rangeStart < slot.end && rangeEnd > slot.start);

    const isCellClosed = (cellStart: number, cellEnd: number) =>
        closedSlots.some((slot: { start: number; end: number }) => cellStart >= slot.start && cellEnd <= slot.end);

    const isCellSelected = (cellStart: number, cellEnd: number) => {
        if (!selectedRange) return false;
        return cellStart >= selectedRange.start && cellEnd <= selectedRange.end;
    };

    const handleCellClick = (cellStart: number, cellEnd: number) => {
        if (!setSelectedRange) return;

        setSelectedRange((prev) => {
            if (!prev) {
                return { start: cellStart, end: cellEnd };
            }

            if (cellStart >= prev.start && cellEnd <= prev.end) {
                return undefined;
            }

            return {
                start: Math.min(prev.start, cellStart),
                end: Math.max(prev.end, cellEnd),
            };
        });
    };

    const dayWindow = useMemo(() => {
        const configuredStart = Number(settingsAgenda?.minHour);
        const configuredEnd = Number(settingsAgenda?.maxHour);
        const hasConfiguredRange =
            Number.isInteger(configuredStart) &&
            Number.isInteger(configuredEnd) &&
            configuredStart >= 0 &&
            configuredEnd <= fallbackEndMinute &&
            configuredStart < configuredEnd;

        let startMinute = hasConfiguredRange ? configuredStart : fallbackStartMinute;
        let endMinute = hasConfiguredRange ? configuredEnd : fallbackEndMinute;

        if (date) {
            // Agenda interna permite horarios flexiveis fora da grade; estende a regua
            // para nao cortar agendamentos existentes fora dos limites configurados.
            const dayBase = moment(date.toDate()).startOf('day');
            for (const appt of sorted) {
                const startMin = calendarMinuteOffset(appt.schedule.startAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                if (startMin < startMinute) startMinute = Math.max(0, startMin);
                const endMin = calendarMinuteOffset(appt.schedule.endAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                if (endMin > endMinute) endMinute = Math.min(fallbackEndMinute, endMin);
            }
        }

        return { startMinute, endMinute: Math.max(startMinute + safeGap, endMinute) };
    }, [date, fallbackEndMinute, fallbackStartMinute, safeGap, settingsAgenda?.maxHour, settingsAgenda?.minHour, sorted]);

    const startMinute = dayWindow.startMinute;
    const endMinute = dayWindow.endMinute;

    const appointmentRanges = useMemo(() => {
        if (!date) return [];

        const dayBase = moment(date.toDate()).startOf('day');

        return sorted
            .map((appt) => {
                const startMin = calendarMinuteOffset(appt.schedule.startAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                const endMin = calendarMinuteOffset(appt.schedule.endAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                const clampedStart = Math.max(startMinute, Math.min(endMinute, startMin));
                const clampedEnd = Math.max(startMinute, Math.min(endMinute, endMin));

                if (clampedEnd <= clampedStart) {
                    return null;
                }

                return {
                    appointmentId: String(appt.schedule?._id || appt.startDate.toISOString()),
                    start: clampedStart,
                    end: clampedEnd,
                };
            })
            .filter(Boolean) as Array<{ appointmentId: string; start: number; end: number }>;
    }, [date, endMinute, sorted, startMinute]);

    const layoutAppointmentRanges = useMemo(() => {
        if (!date) return [];

        const dayBase = moment(date.toDate()).startOf('day');

        return sorted
            .map((appt) => {
                const appointmentId = String(appt.schedule?._id || appt.startDate.toISOString());
                const startMin = calendarMinuteOffset(appt.schedule.startAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                const endMin = calendarMinuteOffset(appt.schedule.endAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                const originalClampedStart = Math.max(startMinute, Math.min(endMinute, startMin));
                const originalClampedEnd = Math.max(startMinute, Math.min(endMinute, endMin));
                const durationMinutes = originalClampedEnd - originalClampedStart;

                if (durationMinutes <= 0) {
                    return null;
                }

                const effectiveStart =
                    dragState?.appointmentId === appointmentId
                        ? dragState.previewStart
                        : originalClampedStart;
                const effectiveEnd = effectiveStart + durationMinutes;

                return {
                    appointmentId,
                    schedule: appt.schedule,
                    start: effectiveStart,
                    end: effectiveEnd,
                    durationMinutes,
                };
            })
            .filter(Boolean) as Array<{
            appointmentId: string;
            schedule: any;
            start: number;
            end: number;
            durationMinutes: number;
        }>;
    }, [date, dragState?.appointmentId, dragState?.previewStart, endMinute, sorted, startMinute]);

    const slots = useMemo(() => {
        const list: number[] = [];
        for (let m = startMinute; m < endMinute; m += safeGap) {
            list.push(m);
        }
        return list;
    }, [endMinute, safeGap, startMinute]);

    const minuteHeights = useMemo(() => {
        const emptySlotHeightPx = 24;
        const occupiedBaseSlotHeightPx = 24;
        const totalMinutes = Math.max(0, endMinute - startMinute);
        if (!date) {
            return Array.from({ length: totalMinutes }, () => occupiedBaseSlotHeightPx / safeGap);
        }

        const emptyPxPerMinute = emptySlotHeightPx / safeGap;
        const occupiedBasePxPerMinute = occupiedBaseSlotHeightPx / safeGap;
        const heights = Array.from({ length: totalMinutes }, () => emptyPxPerMinute);

        for (const appt of layoutAppointmentRanges) {
            const clampedStart = appt.start;
            const clampedEnd = appt.end;
            const durationMinutes = appt.durationMinutes;
            if (durationMinutes <= 0) continue;

            const subscription = appt.schedule?.subscription || null;
            const hasSubscription = Boolean(subscription?.assinaturaId || subscription?.planName);
            const saleOpportunity = appt.schedule?.saleOpportunity || null;
            const saleOpportunityEstimatedTotal = Number(saleOpportunity?.estimatedTotal || 0);
            const hasSaleOpportunity =
                Number(saleOpportunity?.activeRecurringCount || 0) > 0 ||
                saleOpportunityEstimatedTotal > 0;
            const hasBadges =
                Boolean(appt.schedule?.cancellation?.date) ||
                hasSubscription ||
                hasSaleOpportunity;
            const hasObservation = Boolean(String(appt.schedule?.observation || '').trim());
            const minCardHeightPx = hasObservation
                ? (isMobileViewport ? 120 : 104)
                : hasBadges
                  ? (isMobileViewport ? 112 : 96)
                  : 72;
            const requiredPxPerMinute = Math.max(
                occupiedBasePxPerMinute,
                minCardHeightPx / durationMinutes
            );

            // O minimo cresce so nos minutos realmente ocupados pelo agendamento,
            // evitando "rabos" vazios muito grandes dentro de slots longos.
            for (let minute = clampedStart; minute < clampedEnd; minute += 1) {
                const minuteIdx = minute - startMinute;
                if (minuteIdx < 0 || minuteIdx >= heights.length) continue;
                heights[minuteIdx] = Math.max(
                    heights[minuteIdx],
                    requiredPxPerMinute
                );
            }
        }

        return heights;
    }, [date, endMinute, isMobileViewport, layoutAppointmentRanges, safeGap, startMinute]);

    const rowHeights = useMemo(() => {
        return slots.map((slotMinute) => {
            const slotOffset = slotMinute - startMinute;
            let heightPx = 0;

            for (let minuteIdx = slotOffset; minuteIdx < slotOffset + safeGap; minuteIdx += 1) {
                if (minuteIdx < 0 || minuteIdx >= minuteHeights.length) continue;
                heightPx += minuteHeights[minuteIdx];
            }

            return heightPx;
        });
    }, [minuteHeights, safeGap, slots, startMinute]);

    const minuteOffsets = useMemo(() => {
        const offsets = [0];
        let acc = 0;
        for (const height of minuteHeights) {
            acc += height;
            offsets.push(acc);
        }
        return offsets;
    }, [minuteHeights]);

    const totalHeight = useMemo(() => minuteOffsets[minuteOffsets.length - 1] || 0, [minuteOffsets]);

    const minuteToOffset = useMemo(() => {
        return (minute: number) => {
            if (minute <= startMinute) return 0;
            if (minute >= endMinute) return totalHeight;

            const minuteIdx = Math.max(
                0,
                Math.min(minuteOffsets.length - 1, minute - startMinute)
            );
            return minuteOffsets[minuteIdx] ?? totalHeight;
        };
    }, [endMinute, minuteOffsets, startMinute, totalHeight]);

    const offsetToMinute = useMemo(() => {
        return (offset: number) => {
            const clampedOffset = Math.max(0, Math.min(totalHeight, offset));
            let low = 0;
            let high = minuteOffsets.length - 1;

            while (low < high) {
                const mid = Math.floor((low + high) / 2);
                if ((minuteOffsets[mid] ?? 0) < clampedOffset) {
                    low = mid + 1;
                } else {
                    high = mid;
                }
            }

            const upperIdx = Math.min(minuteOffsets.length - 1, low);
            const lowerIdx = Math.max(0, upperIdx - 1);
            const lowerOffset = minuteOffsets[lowerIdx] ?? 0;
            const upperOffset = minuteOffsets[upperIdx] ?? totalHeight;
            const minuteIdx =
                Math.abs(clampedOffset - lowerOffset) <= Math.abs(upperOffset - clampedOffset)
                    ? lowerIdx
                    : upperIdx;

            return startMinute + Math.min(Math.max(minuteIdx, 0), Math.max(0, endMinute - startMinute));
        };
    }, [endMinute, minuteOffsets, startMinute, totalHeight]);

    const syncDragState = (next: DragState | null) => {
        dragStateRef.current = next;
        setDragState(next);
    };

    const clampDraggedStartMinute = (minute: number, durationMinutes: number) => {
        const snappedMinute =
            startMinute + Math.round((minute - startMinute) / safeGap) * safeGap;
        const maxStartMinute = Math.max(startMinute, endMinute - durationMinutes);

        return Math.max(startMinute, Math.min(maxStartMinute, snappedMinute));
    };

    const validateDropTarget = useCallback((
        appointmentId: string,
        nextStartMinute: number,
        durationMinutes: number
    ): DropValidationResult => {
        const nextEndMinute = nextStartMinute + durationMinutes;

        const hasConflict = appointmentRanges.some((range) => {
            if (range.appointmentId === appointmentId) {
                return false;
            }

            return nextStartMinute < range.end && nextEndMinute > range.start;
        });

        if (hasConflict) {
            return { allowed: false, reason: 'conflict' };
        }

        return { allowed: true };
    }, [appointmentRanges]);

    const canDragAppointments = Boolean(date && !isMobileViewport && onAppointmentDrop);
    const hasActiveDrag = Boolean(dragState);

    const nowIndicator = useMemo(() => {
        if (!date) return null;

        const dayStart = moment(date.toDate()).startOf('day');
        const now = zonedDate(nowMinuteTick, timezone).startOf('minute');
        if (now.format('YYYY-MM-DD') !== dayStart.format('YYYY-MM-DD')) return null;

        const nowMin = now.hour() * 60 + now.minute();
        if (nowMin < startMinute || nowMin > endMinute) return null;

        return {
            top: minuteToOffset(nowMin),
            label: now.format('HH:mm'),
        };
    }, [date, endMinute, minuteToOffset, nowMinuteTick, startMinute, timezone]);

    useEffect(() => {
        let timeoutId: number | undefined;

        const syncNowIndicator = () => {
            setNowMinuteTick(moment().startOf('minute').valueOf());

            const now = moment();
            const msUntilNextMinute =
                (60 - now.seconds()) * 1000 - now.milliseconds() + 20;

            timeoutId = window.setTimeout(syncNowIndicator, msUntilNextMinute);
        };

        syncNowIndicator();

        return () => {
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    // ====== AUTO SCROLL (SÓ 1 VEZ) + NÃO “PUXA” DE VOLTA SE USUÁRIO MEXER ======
    const didScrollOnceRef = useRef(false);
    const userInteractedRef = useRef(false);

    useEffect(() => {
        const markInteracted = () => {
            userInteractedRef.current = true;
        };

        window.addEventListener('wheel', markInteracted, { passive: true });
        window.addEventListener('touchstart', markInteracted, { passive: true });
        window.addEventListener('keydown', markInteracted);

        return () => {
            window.removeEventListener('wheel', markInteracted);
            window.removeEventListener('touchstart', markInteracted);
            window.removeEventListener('keydown', markInteracted);
        };
    }, []);

    useEffect(() => {
        if (didScrollOnceRef.current) return;

        if (window.scrollY > 20) {
            didScrollOnceRef.current = true;
            return;
        }

        if (!nowIndicator) {
            didScrollOnceRef.current = true;
            return;
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (userInteractedRef.current) return;
                const el = document.querySelector('[data-native-now-indicator="true"]') as HTMLElement | null;
                if (!el) return;
                const headerOffset = 80;
                const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
                window.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
                didScrollOnceRef.current = true;
            });
        });
    }, [nowIndicator]);
    // ========================================================================

    useEffect(() => {
        if (!dragStateRef.current) {
            return;
        }

        const previousUserSelect = document.body.style.userSelect;
        const previousCursor = document.body.style.cursor;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';

        const handlePointerMove = (event: PointerEvent) => {
            const currentDrag = dragStateRef.current;
            if (!currentDrag || currentDrag.phase === 'dropping' || event.pointerId !== currentDrag.pointerId) {
                return;
            }

            const deltaY = event.clientY - currentDrag.initialPointerY;
            const hasMoved = currentDrag.hasMoved || Math.abs(deltaY) >= 6;
            const rawTop = currentDrag.initialTop + deltaY;
            const nextPreviewStart = clampDraggedStartMinute(
                offsetToMinute(rawTop),
                currentDrag.durationMinutes
            );
            const nextValidation = validateDropTarget(
                currentDrag.appointmentId,
                nextPreviewStart,
                currentDrag.durationMinutes
            );

            if (
                nextPreviewStart === currentDrag.previewStart &&
                hasMoved === currentDrag.hasMoved &&
                nextValidation.allowed === currentDrag.isValidDrop
            ) {
                return;
            }

            if (hasMoved) {
                suppressClickAppointmentIdRef.current = currentDrag.appointmentId;
            }

            document.body.style.cursor = nextValidation.allowed ? 'grabbing' : 'not-allowed';
            const previewStartToApply = nextValidation.allowed
                ? nextPreviewStart
                : currentDrag.previewStart;

            syncDragState({
                ...currentDrag,
                previewStart: previewStartToApply,
                hasMoved,
                isValidDrop: nextValidation.allowed,
            });
        };

        const finishDrag = async (cancelled = false) => {
            const currentDrag = dragStateRef.current;
            if (!currentDrag || currentDrag.phase === 'dropping') {
                return;
            }

            if (
                cancelled ||
                !currentDrag.hasMoved ||
                currentDrag.previewStart === currentDrag.originalStart ||
                !date ||
                !onAppointmentDrop
            ) {
                syncDragState(null);
                return;
            }

            if (!currentDrag.isValidDrop) {
                const invalidReason = validateDropTarget(
                    currentDrag.appointmentId,
                    currentDrag.previewStart,
                    currentDrag.durationMinutes
                ).reason;

                if (invalidReason) {
                    onInvalidAppointmentDrop?.({
                        schedule: currentDrag.schedule,
                        reason: invalidReason,
                    });
                }

                syncDragState(null);
                return;
            }

            syncDragState({
                ...currentDrag,
                phase: 'dropping',
            });

            try {
                await onAppointmentDrop({
                    schedule: currentDrag.schedule,
                    startAt: moment(date.toDate()).startOf('day').add(currentDrag.previewStart, 'minutes').toDate(),
                    localDate: date.format('YYYY-MM-DD'),
                    startMinutes: currentDrag.previewStart,
                    durationMinutes: currentDrag.durationMinutes,
                });
            } finally {
                syncDragState(null);
            }
        };

        const handlePointerUp = (event: PointerEvent) => {
            if (!dragStateRef.current || event.pointerId !== dragStateRef.current.pointerId) {
                return;
            }
            void finishDrag(false);
        };

        const handlePointerCancel = (event: PointerEvent) => {
            if (!dragStateRef.current || event.pointerId !== dragStateRef.current.pointerId) {
                return;
            }
            void finishDrag(true);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerCancel);

        return () => {
            document.body.style.userSelect = previousUserSelect;
            document.body.style.cursor = previousCursor;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerCancel);
        };
    }, [
        date,
        endMinute,
        hasActiveDrag,
        offsetToMinute,
        onAppointmentDrop,
        onInvalidAppointmentDrop,
        safeGap,
        startMinute,
        validateDropTarget,
    ]);

    return (
        <div className="native-schedule-surface mt-4 pb-3">
            <div className="grid grid-cols-[42px,1fr] gap-0 sm:grid-cols-[56px,1fr]">
                <div className="relative select-none" style={{ height: `${totalHeight}px` }}>
                    {slots.map((minute, idx) => (
                        <div
                            key={minute}
                            className="relative pl-0.5 sm:pl-1 pr-0 text-xs overflow-visible"
                            style={{ height: `${rowHeights[idx] ?? 24}px` }}
                        >
                            {(() => {
                                const withinClosed = isMinuteWithinClosedSlot(minute);
                                const textClass = withinClosed
                                    ? 'text-error/65'
                                    : 'text-base-content/55';

                                return (
                            <span
                                className={`absolute inset-x-0 top-0 -translate-y-1/2 pl-0.5 pr-0 text-left text-[11px] font-medium tabular-nums sm:pl-1 sm:text-xs ${textClass}`}
                            >
                                {moment.utc().startOf('day').add(minute, 'minutes').format('HH:mm')}
                            </span>
                                );
                            })()}
                        </div>
                    ))}
                    <span className="absolute inset-x-0 bottom-0 translate-y-1/2 pl-0.5 pr-0 text-left text-[11px] font-medium tabular-nums text-base-content/55 sm:pl-1 sm:text-xs">
                        24:00
                    </span>
                </div>

                <div className="relative overflow-hidden">
                    <div
                        className="relative"
                        style={{ height: `${totalHeight}px` }}
                    >
                        {slots.map((minute, idx) => {
                            const cellStart = minute;
                            const cellEnd = minute + safeGap;
                            const withinWorking = isRangeOverlappingWorkingSlot(cellStart, cellEnd);
                            const closed = isCellClosed(cellStart, cellEnd);
                            const selected = isCellSelected(cellStart, cellEnd);

                            return (
                                <div
                                    key={minute}
                                    role="button"
                                    tabIndex={-1}
                                    onClick={() => handleCellClick(cellStart, cellEnd)}
                                    className={`cursor-pointer border-b ${minute % 60 === 0 ? 'border-base-content/10' : 'border-base-content/5'} ${
                                        selected
                                            ? 'bg-primary/10'
                                            : closed
                                              ? 'bg-error/5'
                                              : withinWorking
                                                ? 'bg-emerald-50/60 dark:bg-emerald-500/5'
                                                : 'bg-transparent'
                                    }`}
                                    style={{ height: `${rowHeights[idx] ?? 24}px` }}
                                />
                            );
                        })}

                        {nowIndicator && (
                            <div
                                data-native-now-indicator="true"
                                className="pointer-events-none absolute left-0 right-0 z-20"
                                style={{ top: `${nowIndicator.top}px` }}
                            >
                                <div className="absolute inset-x-0 top-0 -translate-y-1/2">
                                    <div className="h-px bg-primary/75" />
                                </div>
                                <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
                                    <span className="inline-flex h-5 min-w-[104px] items-center justify-center rounded-full">
                                        <span className="inline-flex h-full w-full items-center justify-center gap-1.5 rounded-full bg-primary px-3 text-[10px] font-semibold leading-none text-primary-content shadow-sm">
                                            <span>Agora</span>
                                            <span className="font-mono tabular-nums">
                                                {nowIndicator.label}
                                            </span>
                                        </span>
                                    </span>
                                </div>
                            </div>
                        )}

                        {sorted.length === 0 && (
                            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-base-content/60">
                                Sem agendamentos para este dia.
                            </div>
                        )}

                        {sorted.length > 0 && (
                            <div className="absolute inset-0 z-10 pointer-events-none">
                                {sorted.map((appt, index) => {
                                    if (!date) {
                                        return (
                                            <div
                                                key={String(appt.schedule?._id || appt.startDate.toISOString())}
                                                className="pointer-events-auto"
                                            >
                                                {renderAppointment({ schedule: appt.schedule })}
                                            </div>
                                        );
                                    }

                                    const dayBase = moment(date.toDate()).startOf('day');
                                    const startMin = calendarMinuteOffset(appt.schedule.startAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);
                                    const endMin = calendarMinuteOffset(appt.schedule.endAt, dayBase.format('YYYY-MM-DD'), appt.schedule.timezone || timezone);

                                    const clampedStart = Math.max(startMinute, Math.min(endMinute, startMin));
                                    const clampedEnd = Math.max(startMinute, Math.min(endMinute, endMin));
                                    if (clampedEnd <= clampedStart) return null;

                                    const appointmentId = String(
                                        appt.schedule?._id || appt.startDate.toISOString()
                                    );
                                    const durationMinutes = clampedEnd - clampedStart;
                                    const layoutRange = layoutAppointmentRanges.find(
                                        (range) => range.appointmentId === appointmentId
                                    );
                                    if (!layoutRange) return null;

                                    const top = minuteToOffset(layoutRange.start);
                                    const bottom = minuteToOffset(layoutRange.end);
                                    const height = bottom - top;
                                    if (height <= 0) return null;

                                    const getOffsetsFor = (target: AgendaAppointment) => {
                                        const targetId = String(
                                            target.schedule?._id || target.startDate.toISOString()
                                        );
                                        const targetRange = layoutAppointmentRanges.find(
                                            (range) => range.appointmentId === targetId
                                        );
                                        if (!targetRange) return null;

                                        return {
                                            clampedStart: targetRange.start,
                                            clampedEnd: targetRange.end,
                                            top: minuteToOffset(targetRange.start),
                                            bottom: minuteToOffset(targetRange.end),
                                        };
                                    };

                                    // Quando dois agendamentos sao contiguos (fim == inicio), as bordas se sobrepoem e
                                    // parecem "grossas". Removemos a borda superior do card de baixo para evitar linha dupla.
                                    const prevOffsets =
                                        index > 0 ? getOffsetsFor(sorted[index - 1]) : null;
                                    const nextOffsets =
                                        index < sorted.length - 1 ? getOffsetsFor(sorted[index + 1]) : null;
                                    const stackedTop = Boolean(
                                        prevOffsets && prevOffsets.clampedEnd === layoutRange.start
                                    );
                                    const stackedBottom = Boolean(
                                        nextOffsets && nextOffsets.clampedStart === layoutRange.end
                                    );
                                    const isDraggingCurrentAppointment =
                                        dragState?.appointmentId === appointmentId;
                                    const isInvalidDragTarget =
                                        isDraggingCurrentAppointment && dragState?.isValidDrop === false;
                                    const renderedTop = top;

                                    const handleAppointmentPointerDown = (
                                        event: React.PointerEvent<HTMLDivElement>
                                    ) => {
                                        if (
                                            !canDragAppointments ||
                                            event.button !== 0 ||
                                            !event.isPrimary ||
                                            dragStateRef.current
                                        ) {
                                            return;
                                        }

                                        const interactiveTarget = (event.target as HTMLElement | null)?.closest(
                                            'button,a,input,textarea,select,label'
                                        );
                                        if (interactiveTarget) {
                                            return;
                                        }

                                        event.preventDefault();
                                        setSelectedRange?.(undefined);
                                        suppressClickAppointmentIdRef.current = null;

                                        syncDragState({
                                            pointerId: event.pointerId,
                                            appointmentId,
                                            schedule: appt.schedule,
                                            originalStart: clampedStart,
                                            previewStart: clampedStart,
                                            durationMinutes,
                                            initialPointerY: event.clientY,
                                            initialTop: top,
                                            heightPx: height,
                                            hasMoved: false,
                                            isValidDrop: true,
                                            phase: 'dragging',
                                        });
                                    };

                                    const handleAppointmentClickCapture = (
                                        event: React.MouseEvent<HTMLDivElement>
                                    ) => {
                                        if (suppressClickAppointmentIdRef.current !== appointmentId) {
                                            return;
                                        }

                                        event.preventDefault();
                                        event.stopPropagation();
                                        suppressClickAppointmentIdRef.current = null;
                                    };

                                    return (
                                        <div
                                            key={appointmentId}
                                            className={`absolute left-0 right-0 pointer-events-none ${
                                                isDraggingCurrentAppointment ? 'z-30' : ''
                                            }`}
                                            style={{ top: `${renderedTop}px`, height: `${height}px` }}
                                        >
                                            <div
                                                className={`pointer-events-auto h-full ${
                                                    canDragAppointments
                                                        ? isDraggingCurrentAppointment
                                                            ? isInvalidDragTarget
                                                                ? 'cursor-not-allowed'
                                                                : 'cursor-grabbing'
                                                            : 'cursor-grab'
                                                        : ''
                                                }`}
                                                onPointerDown={handleAppointmentPointerDown}
                                                onClickCapture={handleAppointmentClickCapture}
                                            >
                                                {renderAppointment({
                                                    schedule: appt.schedule,
                                                    layout: {
                                                        heightPx: height,
                                                        topPx: renderedTop,
                                                        isMobileViewport,
                                                        stackedTop,
                                                        stackedBottom,
                                                    },
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
